import {ArrowLeftIcon} from "@heroicons/react/24/outline";
import {useNavigate, useParams} from "react-router-dom";
import html2pdf from "html2pdf.js";
import AppLayout from "../../../layouts/AdminLayout";
import axios from "axios";
import {useQuery} from "react-query";
import {OrderData} from "./OrderDetail";
import {useState} from "react";

export const OrderReceipt = () => {
  const {orderID} = useParams<{orderID: string}>();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const navigate = useNavigate();
  const [localQueueItems, setLocalQueueItems] = useState<any[]>([]);

  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;
  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  const fetchOrder = async () => {
    const response = await axios.get(
      `${Baseurl}/api/admin/orders/${orderID}?page=1&limit=10`,
      config
    );
    return response?.data?.data.items;
  };

  const {
    data: orderData,
    isLoading,
    refetch,
  } = useQuery<OrderData>(["ORDER", orderID], fetchOrder, {
    enabled: !!orderID,
  });

  const fetchQueue = async () => {
    const response = await axios.get(
      `${Baseurl}/api/admin/orders/${orderID}/topups/queue?page=1&limit=100`,
      config
    );
    const queue = response?.data?.data.items.filter(
      (data: any) => data.orderId === orderID
    );
    setLocalQueueItems(queue[0]?.items);
    return response?.data?.data;
  };

  const {
    data: queueData,
    isLoading: isLoadingQueue,
    isRefetching: isRefetchingQueue,
    refetch: refetchQueue,
  } = useQuery<any>(["QUEUE", orderID], fetchQueue, {
    enabled: !!orderID,
    cacheTime: 0,
  });

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("receipt-pdf");
    if (!element || !orderData) return;

    const clonedElement = element.cloneNode(true) as HTMLElement;
    document.body.appendChild(clonedElement);

    const opt = {
      margin: 0,
      filename: `Order receipt for - ${orderData.orderId}.pdf`,
      html2canvas: {
        scale: 2,
        logging: true,
        useCORS: true,
        allowTaint: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf().from(clonedElement).set(opt).save();
  };

  const handlePrintPDF = () => {
    const element = document.getElementById("receipt-pdf");
    if (!element || !orderData) return;

    const clonedElement = element.cloneNode(true) as HTMLElement;
    document.body.appendChild(clonedElement);

    const opt = {
      margin: 0,
      filename: `Order receipt for - ${orderData.orderId}.pdf`,
      html2canvas: {
        scale: 2,
        logging: true,
        useCORS: true,
        allowTaint: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf()
      .from(clonedElement)
      .set(opt)
      .outputPdf("dataurlnewwindow")
      .finally(() => {
        document.body.removeChild(clonedElement);
      });
  };

  if (!orderData) return null;

  return (
    <AppLayout>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem",
        }}
      >
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "1.5rem",
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowLeftIcon width={24} height={24} color="#666D80" />
          <p style={{fontSize: "0.875rem", color: "#666d80"}}>Go Back</p>
        </button>
      </div>
      <h1
        style={{
          fontSize: "1.75rem",
          fontWeight: "bold",
          color: "#374151",
          textAlign: "center",
        }}
      >
        Order Receipt
      </h1>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          padding: "1rem",
        }}
      >
        <button
          style={{
            background: "white",
            color: "#FF7A00",
            border: "1px solid #FF7A00",
            borderRadius: "0.375rem",
            padding: "0.75rem 1.5rem",
            fontSize: "0.875rem",
            fontWeight: "600",
          }}
          onClick={handleDownloadPDF}
        >
          Download Receipt
        </button>
        <button
          style={{
            background: "#FF7A00",
            color: "white",
            borderRadius: "0.375rem",
            padding: "0.75rem 1.5rem",
            fontSize: "0.875rem",
            fontWeight: "600",
          }}
          onClick={handlePrintPDF}
        >
          Print Receipt
        </button>
      </div>

      <div
        id="receipt-pdf"
        style={{
          background: "white",
          borderRadius: "0.375rem",
          boxShadow: "0px 12px 20px 0px #25314C26",
          padding: "2.5rem",
          margin: "0 auto",
          maxWidth: "42rem",
          marginTop: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1rem",
            gap: "1.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              width: "50%",
              height: "100px",
              border: "1px solid #31EAF4",
              borderRadius: "0.5rem",
            }}
          >
            <img
              src="/logo.png"
              alt="eFTD Africa Logo"
              style={{height: "2rem", marginRight: "0.5rem"}}
            />
            <h2 style={{fontSize: "1.25rem", fontWeight: "600", color: "#333"}}>
              eFTD Africa
            </h2>
          </div>
          <div
            style={{
              width: "50%",
              boxShadow: "0px 2px 19px 0px #305F9712",
              padding: "1rem",
              border: "1px solid #EBEFF6",
              borderRadius: "0.5rem",
              display: "flex",
            }}
          >
            <p
              style={{
                fontSize: "0.75rem",
                color: "#5D6481",
                display: "flex",
                flexDirection: "column",
                paddingRight: "1rem",
                borderRight: "1px solid #EBEFF6",
                width: "50%",
              }}
            >
              <span>Created On: </span>
              <span
                style={{
                  fontWeight: "600",
                  fontSize: "0.75rem",
                  color: "#19213D",
                }}
              >
                {formatDate(orderData.createdAt)}
              </span>
            </p>
            <p
              style={{
                fontSize: "0.75rem",
                color: "#5D6481",
                paddingLeft: "1rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <span>Order Number: </span>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#01CCD9",
                }}
              >
                {orderData.orderId}
              </span>
            </p>
          </div>
        </div>

        <hr style={{margin: "1.5rem 0", borderColor: "#EBEFF6"}} />

        <h3
          style={{
            fontSize: "0.75rem",
            color: "#19213D",
            marginBottom: "0.5rem",
          }}
        >
          Sender Information
        </h3>
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.5rem 1rem",
            background: "#F6F8FC",
            borderRadius: "0.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "0.75rem",
              color: "#666D80",
            }}
          >
            <p>{orderData?.user?.businessName}</p>
            <div
              style={{
                margin: "0 1rem",
                borderLeft: "1px solid #D1D5DB",
                height: "1rem",
              }}
            ></div>
            <p>{orderData.pickUpDetail.phoneNumber}</p>
            <div
              style={{
                margin: "0 1rem",
                borderLeft: "1px solid #D1D5DB",
                height: "1rem",
              }}
            ></div>
            <p>{orderData.pickUpDetail.fullAddress.split(",")[0]}</p>
          </div>
        </div>

        <div style={{marginTop: "1rem"}}>
          {orderData.deliveryDetails.map((delivery, index) => {
            return (
              <div
                key={index}
                style={{
                  padding: "1rem",
                  marginBottom: "1rem",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.375rem",
                }}
              >
                {/* Delivery Information */}
                <h3
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#818898",
                    marginBottom: "0.5rem",
                  }}
                >
                  Delivery Information
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "1rem",
                    fontSize: "0.875rem",
                    color: "#666D80",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Sender
                    </p>
                    <p style={{fontWeight: "600", fontSize: "0.75rem"}}>
                      {orderData?.user?.businessName}
                    </p>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {orderData.pickUpDetail.fullAddress}
                    </p>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {orderData.pickUpDetail.phoneNumber}
                    </p>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Receiver
                    </p>
                    <p style={{fontWeight: "600", fontSize: "0.75rem"}}>
                      {delivery.recipientName}
                    </p>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {delivery.fullAddress}
                    </p>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {delivery.phoneNumber}
                    </p>
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Waybill no. {delivery.waybillNumber || "N/A"}
                    </p>
                    {/* {orderData.deliveryDetails.length > 1 &&
                      delivery.amount && ( */}
                    <p
                      style={{
                        fontSize: "0.625rem",
                        fontWeight: "600",
                        color: "#818898",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Amount: N{Number(delivery.amount).toLocaleString()}
                    </p>
                    {/* )} */}
                  </div>
                </div>

                {/* Product Info */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.625rem",
                    color: "#5D6481",
                    margin: "1rem 0",
                    padding: "0.5rem 1rem",
                    background: "#F6F8FC",
                    borderRadius: "0.5rem",
                  }}
                >
                  <p>
                    Product Weight -{" "}
                    <span style={{fontWeight: "600"}}>
                      {delivery.itemWeight}
                    </span>{" "}
                  </p>
                  <div
                    style={{
                      margin: "0 1rem",
                      borderLeft: "1px solid #D1D5DB",
                      height: "1rem",
                    }}
                  ></div>
                  <p>{delivery.itemDescription}</p>
                </div>

                {/* Amounts Section */}
                {/* {orderData.deliveryDetails.length > 1 ? ( */}
                <>
                  {localQueueItems?.map((queueItem) => {
                    if (
                      queueItem.waybillNumber === delivery.waybillNumber &&
                      queueItem.amount
                    ) {
                      return (
                        <div
                          key={queueItem.waybillNumber}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            margin: "1rem 0",
                            padding: "0.5rem 1rem",
                            background: "#F6F8FC",
                            borderRadius: "0.5rem",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#5D6481",
                            }}
                          >
                            Topup Amount
                          </p>
                          <div
                            style={{
                              margin: "0 1rem",
                              borderLeft: "1px solid #D1D5DB",
                              height: "1rem",
                            }}
                          ></div>
                          <p
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#5D6481",
                            }}
                          >
                            N{Number(queueItem.amount).toLocaleString()}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </>
                {/* ) : (
                  // Single delivery case
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        margin: "1rem 0",
                        padding: "0.5rem 1rem",
                        background: "#F6F8FC",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          color: "#5D6481",
                        }}
                      >
                        Order Amount
                      </p>
                      <div
                        style={{
                          margin: "0 1rem",
                          borderLeft: "1px solid #D1D5DB",
                          height: "1rem",
                        }}
                      ></div>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          color: "#5D6481",
                        }}
                      >
                        N{Number(orderData.estimatedCost).toLocaleString()}
                      </p>
                    </div>

                    {orderData.TopupRequest && orderData.TopupRequest.paid && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          margin: "1rem 0",
                          padding: "0.5rem 1rem",
                          background: "#F6F8FC",
                          borderRadius: "0.5rem",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            color: "#5D6481",
                          }}
                        >
                          Topup Amount
                        </p>
                        <div
                          style={{
                            margin: "0 1rem",
                            borderLeft: "1px solid #D1D5DB",
                            height: "1rem",
                          }}
                        ></div>
                        <p
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "700",
                            color: "#5D6481",
                          }}
                        >
                          N
                          {Number(
                            orderData.TopupRequest?.amount
                          ).toLocaleString()}
                        </p>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        margin: "1rem 0",
                        padding: "0.5rem 1rem",
                        background: "#F6F8FC",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          color: "#5D6481",
                        }}
                      >
                        Total Cost
                      </p>
                      <div
                        style={{
                          margin: "0 1rem",
                          borderLeft: "1px solid #D1D5DB",
                          height: "1rem",
                        }}
                      ></div>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          color: "#5D6481",
                        }}
                      >
                        {orderData.TopupRequest && orderData.TopupRequest.paid
                          ? `N${(
                              Number(orderData.TopupRequest.amount) +
                              Number(orderData.estimatedCost)
                            ).toLocaleString()}`
                          : `N${Number(
                              orderData.estimatedCost
                            ).toLocaleString()}`}
                      </p>
                    </div>
                  </>
                )} */}
              </div>
            );
          })}
          {/* {orderData.deliveryDetails.length > 1 && ( */}
          <>
            {/* Order Amount */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                margin: "1rem 0",
                padding: "0.5rem 1rem",
                background: "#F6F8FC",
                borderRadius: "0.5rem",
              }}
            >
              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#5D6481",
                }}
              >
                Order Amount
              </p>
              <div
                style={{
                  margin: "0 1rem",
                  borderLeft: "1px solid #D1D5DB",
                  height: "1rem",
                }}
              ></div>
              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#5D6481",
                }}
              >
                N{Number(orderData.estimatedCost).toLocaleString()}
              </p>
            </div>

            {/* Total Topups */}
            {localQueueItems && localQueueItems.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  margin: "1rem 0",
                  padding: "0.5rem 1rem",
                  background: "#F6F8FC",
                  borderRadius: "0.5rem",
                }}
              >
                <p
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    color: "#5D6481",
                  }}
                >
                  Total Topups
                </p>
                <div
                  style={{
                    margin: "0 1rem",
                    borderLeft: "1px solid #D1D5DB",
                    height: "1rem",
                  }}
                ></div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    color: "#5D6481",
                  }}
                >
                  N
                  {Number(
                    localQueueItems.reduce(
                      (total, queue) => total + Number(queue.amount || 0),
                      0
                    )
                  ).toLocaleString()}
                </p>
              </div>
            )}

            {/* Total Amount */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                margin: "1rem 0",
                padding: "0.5rem 1rem",
                background: "#F6F8FC",
                borderRadius: "0.5rem",
              }}
            >
              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#5D6481",
                }}
              >
                Total Amount
              </p>
              <div
                style={{
                  margin: "0 1rem",
                  borderLeft: "1px solid #D1D5DB",
                  height: "1rem",
                }}
              ></div>
              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#5D6481",
                }}
              >
                N
                {(
                  Number(orderData.estimatedCost) +
                  (localQueueItems?.length > 0
                    ? Number(
                        localQueueItems?.reduce(
                          (total, queue) => total + Number(queue.amount || 0),
                          0
                        )
                      )
                    : 0)
                ).toLocaleString()}
              </p>
            </div>
          </>
          {/* )} */}
        </div>
      </div>
    </AppLayout>
  );
};
